import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { forexApi, type OperatorOwner } from "@/lib/forex-api";

import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useAgentProfit, useProfitEnvironment } from './use-agent-profit';
import type { TeamProfit } from '@/lib/forex-profit-api';
import { PerformanceValues } from './components/ProfitMetricValue';
import './components/profit/profit-dashboard.css';
import CreateOperatorOwnerDialog from './components/CreateOperatorOwnerDialog';
import { notifyOwnerCatalogChanged } from '@/lib/forex-owner-coordinator';
import { getAccessToken } from '@/lib/auth';
import OperatorOwnerAgentsPanel from './components/OperatorOwnerAgentsPanel';
import PersonInvitationPanel from './components/PersonInvitationPanel';

const statusChip: Record<string, { cls: string; label: string }> = {
  active: { cls: "bg-emerald-500/10 text-emerald-500", label: "已启用" },
  suspended: { cls: "bg-red-500/10 text-red-500", label: "已暂停" },
  pending: { cls: "bg-amber-500/10 text-amber-500", label: "待入驻" },
};

const reviewChip: Record<string, { cls: string; label: string }> = {
  approved: { cls: "bg-emerald-500/10 text-emerald-500", label: "已通过" },
  pending: { cls: "bg-amber-500/10 text-amber-500", label: "待审核" },
  rejected: { cls: "bg-red-500/10 text-red-500", label: "已拒绝" },
};

function formatDate(timestamp: number) {
  return new Date(timestamp * 1000).toLocaleDateString("sv-SE");
}

export default function MerchantsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('tab') === 'members' ? 'members' : searchParams.get('tab') === 'users' ? 'users' : 'org';
  const selectedOwner = searchParams.get('owner_ref') || '';
  const changeView = (next: 'org' | 'members' | 'users', ownerRef = selectedOwner) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', next);
    if (ownerRef) nextParams.set('owner_ref', ownerRef); else nextParams.delete('owner_ref');
    setSearchParams(nextParams);
  };
  const { admin } = useAdminAuth();
  const [creating, setCreating] = useState(false);
  const [creatingParent, setCreatingParent] = useState<string | undefined>();
  const [invitation, setInvitation] = useState<{ owner: OperatorOwner; code: string; registrations?: Awaited<ReturnType<typeof forexApi.getOperatorOwnerRegistrations>> } | null>(null);
  const [highlight, setHighlight] = useState('');
  let canCreate = false;
  try { const token = getAccessToken(); const role = token ? JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).role : ''; canCreate = ['SUPER_ADMIN', 'OPERATOR'].includes(String(role).toUpperCase()); } catch { /* Server role remains authoritative. */ }
  const environment = useProfitEnvironment();
  const profits = useAgentProfit<TeamProfit>({kind:'teams',limit:100});
  const [managing, setManaging] = useState<OperatorOwner>();
  const requestSequence = useRef(0);
  const environmentRef = useRef(environment);
  environmentRef.current = environment;
  const [ownersEnvironment, setOwnersEnvironment] = useState(environment);
  const [owners, setOwners] = useState<OperatorOwner[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [error, setError] = useState("");
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const [collapsedOwners,setCollapsedOwners]=useState<Set<string>>(()=>new Set());

  const showToast = (type: "success" | "error", msg: string) => {
    setToast({ type, msg });
  };

  const loadOwners = useCallback(() => {
    let cancelled = false;
    const sequence = ++requestSequence.current;
    const active = () => !cancelled && sequence === requestSequence.current && environmentRef.current === environment;
    setLoading(true);
    setError("");
    forexApi
      .listOperatorOwners()
      .then((result) => { if (active()) { setOwners(result.items); setOwnersEnvironment(environment); } })
      .catch(() => { if (active()) setError("运营归属方接口不可用，请稍后重试。"); })
      .finally(() => { if (active()) setLoading(false); });
    return () => { cancelled = true; };
  }, [environment]);

  useEffect(() => {
    setManaging(undefined);
    setInvitation(null);
    setCreating(false);
    setHighlight('');
    setSavingId("");
    setOwners([]);
    setCollapsedOwners(new Set());
    setToast(null);
    return loadOwners();
  }, [loadOwners, admin?.id]);

  useEffect(() => {
    if (!highlight || loading) return;
    document.querySelector(`[data-owner-ref="${highlight}"]`)?.scrollIntoView({block: "nearest", behavior: "smooth"});
    const timer = setTimeout(() => setHighlight(""), 5000);
    return () => clearTimeout(timer);
  }, [highlight, loading]);

  const handleStatus = async (owner: OperatorOwner, status: "active" | "suspended") => {
    const operationEnvironment = environment;
    setSavingId(owner.owner_ref);
    try {
      const updated = await forexApi.patchOperatorOwner(owner.owner_ref, {
        status,
        reason: status === "active" ? "admin enable owner" : "admin suspend owner",
      });
      if (environmentRef.current !== operationEnvironment) return;
      notifyOwnerCatalogChanged();
      setOwners((items) => items.map((item) => (item.owner_ref === updated.owner_ref ? updated : item)));
      showToast("success", status === "active" ? `${owner.name} 已启用` : `${owner.name} 已暂停`);
    } catch {
      if (environmentRef.current !== operationEnvironment) return;
      showToast("error", "运营归属方状态更新失败，请确认后端服务和登录态");
    } finally {
      if (environmentRef.current === operationEnvironment) setSavingId("");
    }
  };

  const totals = {
    total: owners.length,
    active: owners.filter((item) => item.status === "active").length,
    agents: profits.data?.items.filter(t=>t.owner_ref!=='__unassigned__').reduce((sum,t)=>sum+t.current_agent_count,0),
  };
  const ownerByRef = new Map(owners.map(owner => [owner.owner_ref, owner]));
  const activePath = (owner: OperatorOwner): boolean => {
    let current: OperatorOwner | undefined = owner;
    const seen = new Set<string>();
    while (current) {
      if (current.status !== 'active' || seen.has(current.owner_ref)) return false;
      seen.add(current.owner_ref);
      if (current.parent_owner_ref && !ownerByRef.has(current.parent_owner_ref)) return false;
      current = current.parent_owner_ref ? ownerByRef.get(current.parent_owner_ref) : undefined;
    }
    return true;
  };
  const availableToCreate = canCreate && (admin?.role === 'super_admin' || owners.some(owner => owner.node_type && activePath(owner)));
  const ownerDepth = (owner: OperatorOwner): number => {
    let depth = 0;
    let parent = owner.parent_owner_ref;
    const seen = new Set([owner.owner_ref]);
    while (parent && ownerByRef.has(parent) && !seen.has(parent)) {
      seen.add(parent);
      depth += 1;
      parent = ownerByRef.get(parent)?.parent_owner_ref;
    }
    return depth;
  };
  const orderedOwners: OperatorOwner[] = [];
  const visit = (owner: OperatorOwner, visited: Set<string>) => {
    if (visited.has(owner.owner_ref)) return;
    visited.add(owner.owner_ref);
    orderedOwners.push(owner);
    owners.filter(child => child.parent_owner_ref === owner.owner_ref).forEach(child => visit(child, visited));
  };
  const visited = new Set<string>();
  owners.filter(owner => !owner.parent_owner_ref || !ownerByRef.has(owner.parent_owner_ref)).forEach(owner => visit(owner, visited));
  owners.forEach(owner => visit(owner, visited));
  const ownerPath = (owner: OperatorOwner): string => {
    const names = [owner.name];
    let parent = owner.parent_owner_ref;
    const seen = new Set([owner.owner_ref]);
    while (parent && ownerByRef.has(parent) && !seen.has(parent)) {
      seen.add(parent); const node = ownerByRef.get(parent)!; names.unshift(node.name); parent = node.parent_owner_ref;
    }
    return names.join(' / ');
  };
  const hiddenByCollapse = (owner: OperatorOwner): boolean => {
    let parent = owner.parent_owner_ref;
    const seen = new Set([owner.owner_ref]);
    while (parent && ownerByRef.has(parent) && !seen.has(parent)) {
      if (collapsedOwners.has(parent)) return true;
      seen.add(parent); parent = ownerByRef.get(parent)?.parent_owner_ref;
    }
    return false;
  };

  const showInvitation = async (owner: OperatorOwner) => {
    const operationEnvironment = environment;
    try {
      const result = await forexApi.getOperatorOwnerInvitation(owner.owner_ref);
      if (environmentRef.current !== operationEnvironment) return;
      setInvitation({ owner, code: result.code });
      try {
        const registrations = await forexApi.getOperatorOwnerRegistrations(owner.owner_ref, owner.node_type === 'company' ? 'company' : 'owner');
        if (environmentRef.current !== operationEnvironment) return;
        setInvitation(current => current?.owner.owner_ref === owner.owner_ref ? { ...current, registrations } : current);
      } catch {
        if (environmentRef.current !== operationEnvironment) return;
        showToast('error', '首次注册来源暂时无法读取');
      }
    } catch {
      if (environmentRef.current !== operationEnvironment) return;
      showToast('error', '邀请码读取失败，请稍后重试');
    }
  };

  if (view !== 'org') return <div className="space-y-4">
    <div><h2 className="text-lg font-semibold text-text-primary">团队人员与邀请</h2><p className="text-xs text-text-muted">按人员查看个人邀请码和首次邀请注册用户</p></div>
    <div className="flex gap-2 border-b border-surface-border pb-2 text-xs">
      <button onClick={() => changeView('org')}>组织与运营</button>
      <button className={view === 'members' ? 'text-brand font-semibold' : ''} onClick={() => changeView('members')}>团队人员</button>
      <button className={view === 'users' ? 'text-brand font-semibold' : ''} onClick={() => changeView('users')}>邀请用户</button>
    </div>
    {loading ? <p>加载中...</p> : error ? <p role="alert">{error}</p> : <>
      {!selectedOwner&&<p className="text-xs text-text-muted">请先选择公司或团队；人员与邀请记录按所选范围展示，不自动汇总到全部归属方。</p>}
      <PersonInvitationPanel key={environment} owners={owners} selectedOwner={selectedOwner}
        onSelectOwner={ref => changeView(view, ref)} onViewChange={next => changeView(next)} view={view}/></>}
  </div>;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div><h2 className="text-lg font-semibold text-text-primary">运营归属管理</h2>
        <p className="text-xs text-text-muted mt-0.5">管理外汇运营归属方，查看主账号、跟单关系及收益贡献</p></div>
        {availableToCreate && <button className="inline-flex min-h-9 items-center gap-1 rounded-lg bg-brand px-3 text-xs font-medium text-white" onClick={() => { setCreatingParent(undefined); setCreating(true); }}><i className="ri-add-line" aria-hidden="true"/>创建公司/团队</button>}
      </div>
      <div className="flex gap-2 border-b border-surface-border pb-2 text-xs">
        <button className="text-brand font-semibold">组织与运营</button>
        <button onClick={() => changeView('members')}>团队人员</button>
        <button onClick={() => changeView('users')}>邀请用户</button>
      </div>
      {creating && availableToCreate && <CreateOperatorOwnerDialog key={`${environment}:${admin?.id}:${creatingParent || ''}`} owners={owners} canCreateCompany={admin?.role === 'super_admin'} initialParent={creatingParent} onClose={() => setCreating(false)} onCreated={owner => { setCreating(false); setHighlight(owner.owner_ref); showToast('success', `“${owner.name}”已创建`); loadOwners(); profits.refresh(); notifyOwnerCatalogChanged(); }}/>}


      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">归属方总数</p><p className="text-sm font-semibold font-mono mt-0.5 text-text-primary">{totals.total}</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">已启用</p><p className="text-sm font-semibold font-mono mt-0.5 text-emerald-500">{totals.active}</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">智能体数量（当前已加载团队）</p><p className="text-sm font-semibold font-mono mt-0.5">{totals.agents ?? "—"}</p></div>
      </div>

      {profits.error && <p role="alert" className="text-red-500">{profits.error}</p>}
      <p className="text-xs text-text-muted">表格每行展示该归属方的当前数据；下级团队单独列出，不把父级与下级表现相加。历史归属请在盈利大盘按交易归属查看。</p>
      <Link to="/forex/profit-dashboard" className="text-brand-600 text-sm">查看全部团队盈利</Link>
      {invitation && <div className="rounded-lg border border-surface-border bg-white p-4 text-xs" role="region" aria-label="团队邀请码">
        <div className="flex items-center justify-between"><strong>{invitation.owner.name} · 邀请码</strong><button onClick={() => setInvitation(null)} aria-label="关闭邀请码">关闭</button></div>
        <p className="mt-2 font-mono text-base tracking-wider">{invitation.code}</p>
        <p className="mt-1 text-text-muted">新用户注册时填写；已注册用户的首次来源不会改变。停用本节点或上级后该码不可用于新注册。</p>
        {!activePath(ownerByRef.get(invitation.owner.owner_ref) || invitation.owner) && <p role="status" className="mt-1 text-amber-600">当前层级已停用，邀请码暂不可用。</p>}
        {invitation.registrations && <div className="mt-3 border-t border-surface-border pt-3">
          <p className="font-medium">{invitation.owner.node_type === 'company' ? '公司及下级团队' : '本团队'}首次邀请注册：{invitation.registrations.total} 人</p>
          {invitation.registrations.items.length === 0 ? <p className="mt-1 text-text-muted">暂无邀请注册用户</p> : <ul className="mt-2 max-h-32 overflow-y-auto space-y-1 text-text-secondary">
            {invitation.registrations.items.map(item => <li key={item.user_id}>{item.email_masked || item.user_id} · {item.path_names.join(' / ')} · {formatDate(item.recorded_at)}</li>)}
          </ul>}
        </div>}
        <button className="mt-2 text-brand" onClick={() => { void navigator.clipboard.writeText(invitation.code).then(() => showToast('success', '邀请码已复制')).catch(() => showToast('error', '复制失败，请手动复制')); }}>复制邀请码</button>
      </div>}
      {ownersEnvironment===environment && managing && <OperatorOwnerAgentsPanel key={environment+managing.owner_ref} owner={managing} owners={owners} onSaved={profits.refresh} onClose={()=>setManaging(undefined)}/>}
      {loading && <div className="bg-white rounded-lg border border-surface-border px-4 py-10 text-center text-sm text-text-muted">加载中...</div>}
      {!loading && error && <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-500">{error}</div>}

      {ownersEnvironment===environment && !loading && !error && (
        <div className="bg-white rounded-lg border border-surface-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[1100px]">
              <thead>
                <tr className="border-b border-surface-border bg-surface/50">
                  <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">归属方</th>
                  <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">状态</th>
                  <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">当前智能体</th>
                  <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">主账号表现</th>
                  <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px]">子账号表现</th>
                  <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">审核</th>
                  <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">联系人</th>
                  <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">入驻时间</th>
                  <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">操作</th>
                </tr>
              </thead>
              <tbody>
                {orderedOwners.filter(owner=>!hiddenByCollapse(owner)).map((owner) => {
                  const profit = profits.data?.items.find(t=>t.owner_ref===owner.owner_ref);
                  const st = statusChip[owner.status] || { cls: "bg-surface text-text-secondary", label: owner.status };
                  const rv = reviewChip[owner.review_status] || { cls: "bg-surface text-text-secondary", label: owner.review_status };
                  return (
                    <tr data-owner-ref={owner.owner_ref} key={owner.owner_ref} className={`border-b border-surface-border hover:bg-surface/30 transition-colors ${highlight === owner.owner_ref ? 'bg-emerald-50' : ''}`}>
                      <td className="px-4 py-2.5" style={{ paddingLeft: `${16 + ownerDepth(owner) * 20}px` }}><div className="flex items-center gap-1">{owners.some(child=>child.parent_owner_ref===owner.owner_ref)&&<button aria-label={`${collapsedOwners.has(owner.owner_ref)?'展开':'折叠'} ${owner.name} 的下级团队`} onClick={()=>setCollapsedOwners(current=>{const next=new Set(current);if(next.has(owner.owner_ref))next.delete(owner.owner_ref);else next.add(owner.owner_ref);return next;})} className="text-brand">{collapsedOwners.has(owner.owner_ref)?'▸':'▾'}</button>}<span className="text-text-primary text-xs font-medium whitespace-nowrap">{ownerDepth(owner) > 0 ? '└ ' : ''}{owner.name}</span><span className="ml-2 text-[10px] text-text-muted">{owner.node_type === 'company' ? '公司' : owner.node_type === 'team' ? '团队' : '未归类'}</span></div><p className="text-[10px] text-text-muted">{ownerPath(owner)}</p><p className="text-text-muted text-[10px] font-mono">{owner.owner_ref}<button className="ml-1 inline-flex min-h-8 items-center px-1" aria-label={`复制 ${owner.name} 的团队标识`} onClick={() => { void navigator.clipboard.writeText(owner.owner_ref).then(() => showToast("success", "团队标识已复制")).catch(() => showToast("error", "复制失败，请手动复制团队标识")); }}><i className="ri-file-copy-line" aria-hidden="true"/></button></p></td>
                      <td className="px-4 py-2.5 text-center"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${st.cls}`}>{st.label}</span></td>
                      <td className="px-4 py-2.5 text-center text-text-primary font-mono text-xs">{profit?.current_agent_count ?? "—"}</td>
                      <td className="px-4 py-2.5 text-center text-text-primary font-mono text-xs">{profit ? <PerformanceValues values={profit.master}/> : "— / 数据未就绪"}</td>
                      <td className="p-3">{profit ? <PerformanceValues values={profit.follower}/> : "— / 数据未就绪"}</td>
                      <td className="px-4 py-2.5 text-center"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${rv.cls}`}>{rv.label}</span></td>
                      <td className="px-4 py-2.5 text-text-secondary text-[11px]">{owner.contact_person}</td>
                      <td className="px-4 py-2.5 text-text-muted text-[11px] whitespace-nowrap">{formatDate(owner.joined_at)}</td>
                      <td className="px-4 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1 flex-wrap">
                          <Link to={`/forex/profit-dashboard?owner_ref=${encodeURIComponent(owner.owner_ref)}`}>查看盈利</Link>
                          {admin?.role==='super_admin'&&<button onClick={()=>setManaging(owner)}>管理智能体</button>}
                          <Link to={`/forex/merchants/detail?id=${owner.owner_ref}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand transition-colors cursor-pointer whitespace-nowrap"><i className="ri-eye-line text-[11px]" />详情</Link>
                          {owner.node_type && <button onClick={() => changeView('members', owner.owner_ref)} className="px-2 py-1 text-brand">人员与邀请</button>}
                          {availableToCreate && owner.node_type && activePath(owner) && <button onClick={() => { setCreatingParent(owner.owner_ref); setCreating(true); }} className="px-2 py-1 text-brand">创建下级</button>}
                          {owner.status === "active" && <button onClick={() => handleStatus(owner, "suspended")} disabled={savingId === owner.owner_ref} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"><i className={`${savingId === owner.owner_ref ? "ri-loader-4-line animate-spin" : "ri-pause-line"} text-[11px]`} />暂停</button>}
                          {owner.status !== "active" && <button onClick={() => handleStatus(owner, "active")} disabled={savingId === owner.owner_ref} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"><i className={`${savingId === owner.owner_ref ? "ri-loader-4-line animate-spin" : "ri-play-line"} text-[11px]`} />启用</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!orderedOwners.length&&<tr><td colSpan={9} className="px-4 py-8 text-center text-text-muted">当前环境没有可显示的运营归属方</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {toast && (
        <div role="status" className="fixed bottom-6 right-6 z-50">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white ${toast.type === "success" ? "bg-emerald-500" : "bg-red-500"}`}><i className={`text-base ${toast.type === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"}`} />{toast.msg}</div>
        </div>
      )}
    </div>
  );
}
